import { useContext, useEffect } from 'react';
import useCustomHook from '@/hooks/useCustomHook';
import { AudioAutoPlayerContext } from '@/shared/Players/AudioAutoPlayer/AudioAutoPlayerContext';
import { useGamificationContext } from '@/shared/Gamification/GamificationContext/GamificationContext';
import useMaximizeScreen from '../../ActivityComponents/MaximizeScreen/useMaximizeScreen.hook';
import useActivitySEO from '../../useActivitySEO.hook';

export default function useSortImagesGameActivity({
  activity,
  activityConf,
  taskProgressActions,
}) {
  const audioPlayerContext = useContext(AudioAutoPlayerContext);
  const gamificationContext = useGamificationContext();
  const { maximize, minimize } = useMaximizeScreen();
  const { actions: seoActions } = useActivitySEO({
    activity,
    activityConf: activityConf,
  });

  const { state, actions } = useCustomHook({
    defaultState: {
      screen: 'COVER',
      activityConf: {
        menu: true,
        miniactivityType: null,
      },
      canRestart: false,
    },
    actions: {
      setGameConf: (conf) => {
        actions.setState({
          activityConf: { ...state.activityConf, ...conf },
        });
      },
      startGame: async () => {
        await actions.taskProgressAPI.START(true);
        seoActions.onStart();

        actions.setState({
          screen: 'GAME',
        });
        maximize();
      },
      restartGame: () => {
        audioPlayerContext.actions.reset();
        taskProgressActions?.onActivityRestarted();
        actions.setState({ canRestart: false });
        maximize();
      },
      goToMainMenu: () => {
        seoActions.reset();
        audioPlayerContext.actions.reset();
        actions.setState({
          screen: 'COVER',
          activityConf: {
            menu: true,
          },
          canRestart: false,
        });
      },
      onFinishedActivity: () => {
        actions.setState({
          canRestart: true,
        });

        seoActions.onFinish();
        minimize();
      },
      taskProgressAPI: {
        START: async (continueFromLastProgress) => {
          if (activityConf && taskProgressActions) {
            const progressData = {
              activitySubType: activity.data.subType,
              miniactivityType: activityConf.miniactivityType,
              progress: 0,
            };
            try {
              actions.setState({
                preparingProgress: true,
              });
              await taskProgressActions.createProgress(
                progressData,
                continueFromLastProgress
              );
              actions.setState({
                preparingProgress: false,
              });
            } catch (error) {
              actions.setState({
                preparingProgress: false,
              });
            }
          }
        },
        PROGRESS: async (progressData, gamificationProgressData) => {
          if (gamificationProgressData) {
            gamificationContext.actions.putData({
              activityId: activity.id,
              miniactivityType: state.activityConf.miniactivityType,
              ...gamificationProgressData,
            });
          }
          if (activityConf && taskProgressActions) {
            if (activityConf.progress) {
              await taskProgressActions.updateProgress({
                activitySubType: activity.data.subType,
                miniactivityType: activityConf.miniactivityType,
                ...progressData,
              });
            }
          }
        },
      },
    },
  });

  useEffect(() => {
    if (activityConf) {
      actions.setState({
        activityConf,
      });
    }
  }, [activityConf]);

  return { state, actions };
}
